import { BODY_TYPEFACE, fontSizes, muiToOrchestraTheme } from '@babushkaui/core';
import { ThemeProvider } from '@mui/material';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { env } from 'process';
import React, { useMemo, useState } from 'react';
import { createGlobalStyle } from 'styled-components';
import { CombinedError, Provider } from 'urql';
import { PagePropsContext } from '~/components/contexts/PageProps';
import { ErrorPopup } from '~/components/error/ErrorPopup';
import { ImpersonatorTag } from '~/components/layout/ImpersonatorTag';
import { SnackbarProvider } from '~/components/provider/snackbar/SnackbarProvider';
import graphQLClient from '~/webClients/graphql';

const UserLogging = React.lazy(() => import('~/webClients/webLogging/user'));

interface EnvProps {
  nodeEnv: string;
  env?: string;
  version?: string;
  webSocketUrl: string;
}

const GlobalStyle = createGlobalStyle({
  html: {
    fontSize: fontSizes.base,
  },
  '@fontface': {
    fontDisplay: 'swap',
    fontFamily: BODY_TYPEFACE,
  },
  body: {
    fontFamily: BODY_TYPEFACE,
    fontSize: fontSizes.base,
    margin: 0,
    backgroundColor: '#FDFDFD', //denomralized
  },
  input: {
    fontSize: fontSizes.base,
  },
  select: {
    backgroundColor: 'unset',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '1rem',
    lineHeight: '1.5',
  },
  th: {
    backgroundColor: '#f4f4f4',
    textAlign: 'left',
    padding: '12px',
    borderBottom: '2px solid #ddd',
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #eee',
  },
  trHover: {
    transition: 'background-color 0.2s',
    cursor: 'pointer',
  },
  trHoverHover: {
    backgroundColor: '#f9f9f9',
  },
  h1: {
    fontFamily: 'neuton',
  },

  h2: {
    fontFamily: 'neuton',
  },

  h3: {
    fontFamily: 'neuton',
  },
});

const MyApp = ({ Component, pageProps, ...props }: AppProps & EnvProps) => {
  const [error, setError] = useState<CombinedError | null>(null);

  const memoClient = useMemo(() => graphQLClient(props.webSocketUrl ?? '', setError), [props.webSocketUrl]);
  const environment = props?.env ?? 'local';

  return (
    <React.Fragment>
      <Head>
        <link rel='stylesheet' href='https://use.typekit.net/yzt1oze.css' />
      </Head>
      <Provider value={memoClient}>
        <UserLogging
          env={environment}
          version={props.version ?? 'local'}
          isEnabled={props?.nodeEnv === 'production'}
          service={`conductor`}
        />
        <ThemeProvider theme={muiToOrchestraTheme}>
          <GlobalStyle />
          <PagePropsContext.Provider value={pageProps}>
            <SnackbarProvider>
              <Component {...pageProps} />
              <ImpersonatorTag />
              <ErrorPopup error={error} />
            </SnackbarProvider>
          </PagePropsContext.Provider>
        </ThemeProvider>
      </Provider>
    </React.Fragment>
  );
};

MyApp.getInitialProps = () => {
  return {
    nodeEnv: process.env.NODE_ENV,
    env: process.env.APP_ENV,
    version: process.env.BUILD,
    webSocketUrl: env.NEXT_PUBLIC_GRAPHQL_SUBSCRIPTIONS_URL,
  } satisfies EnvProps;
};

export default withLDProvider({
  clientSideID: process.env.LAUNCHDARKLY_CLIENT_ID,
  options: {
    inspectors: [
      {
        type: 'flag-used',
        name: 'dd-rum-inspector',
        method: (key, detail) =>
          import('@datadog/browser-rum').then(({ datadogRum }) => datadogRum.addFeatureFlagEvaluation(key, detail.value)),
      },
    ],
  },
})(MyApp as any);
